import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { useAuth } from '@renderer/chat/providers'
import { useEnrichedBilling } from '@renderer/chat/hooks'
import { useAudioNoteSettings } from '@renderer/chat/hooks/useAudioNoteSettings'
import { MEETING_FOLLOW_UPS_QUERY_KEY } from '../../hooks/reminder-query-keys'
import { buildMeetingFollowUps, NO_FOLLOW_UPS, type MeetingFollowUps } from './meeting-follow-ups'

interface FollowUpsResponse {
  data?: { reminders?: Array<unknown> }
}

export interface MeetingFollowUpsResult extends MeetingFollowUps {
  /**
   * False until this note's follow-ups have come back at least once. An empty
   * list and an unanswered one look identical otherwise, and the two callers
   * have to tell them apart: one to avoid a "nothing here" that beats the data,
   * the other to avoid opening the summary and snapping it shut.
   */
  isSettled: boolean
}

/**
 * Whether follow-ups can be generated for this user at all.
 *
 * The same two settings `deriveMeetingSummaryState` consults, read here rather
 * than taken off the summary phase: a user who already has a summary saved
 * resolves to `finalized` whichever way these fall, so the phase can't answer
 * this on its own.
 */
export function useCanDraftFollowUps(): boolean {
  const { isUsingLocalStorage } = useAudioNoteSettings()
  const { isFree } = useEnrichedBilling()
  return !isUsingLocalStorage && !isFree
}

/**
 * Reminders this meeting produced.
 *
 * Fetched from the note's own `?expand=reminders`, because the gateway resolves
 * reminders produced under either the audio note or its live audio session.
 *
 * `view=metadata` avoids fetching the transcript whenever a follow-up changes.
 */
export function useMeetingFollowUps(audioNoteId: string | undefined): MeetingFollowUpsResult {
  const { getAuthToken } = useAuth()

  const { data, isSuccess } = useQuery({
    queryKey: [MEETING_FOLLOW_UPS_QUERY_KEY, audioNoteId],
    queryFn: async () => {
      const token = await getAuthToken()
      const response = await fetch(
        getChatApiRoute(`api/v1/audio-notes/${audioNoteId}?view=metadata&expand=reminders`),
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!response.ok) {
        throw new Error(`Failed to fetch meeting follow-ups: ${response.status}`)
      }
      const json = (await response.json()) as FollowUpsResponse
      return buildMeetingFollowUps(json.data?.reminders)
    },
    enabled: !!audioNoteId,
    staleTime: 60 * 1000,
  })

  // Memoized: this object is a prop on the section, and a fresh identity every
  // render would churn everything under it for nothing.
  return useMemo(() => ({ ...(data ?? NO_FOLLOW_UPS), isSettled: isSuccess }), [data, isSuccess])
}
